import { type AIScopingResponse } from "@/lib/intake-schemas";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  canOfferIntakePaymentHold,
  resolveIntakePaymentHoldPolicy,
  type IntakePaymentHoldPolicy,
} from "@shared/contracts/intake";
import {
  Sparkles,
  Clock,
  AlertTriangle,
  Info,
  ShieldCheck,
  CreditCard,
  FileText,
  CheckCircle2,
} from "lucide-react";

interface PricingInfo {
  currency: string;
  smallTaskFee: number;
  mediumTaskFee: number;
  hourlyRate: number;
  schedulingLinkUrl: string | null;
  intakePaymentHoldPolicy: IntakePaymentHoldPolicy;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

interface AIScopePreviewProps {
  aiScoping: AIScopingResponse;
  isCovered?: boolean;
}

const bucketConfig: Record<string, { label: string; color: string; bg: string }> = {
  small: {
    label: "Quick Fix (<30 min)",
    color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
    bg: "from-emerald-500/60 to-emerald-500/10",
  },
  medium: {
    label: "Medium (~1 hr)",
    color: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    bg: "from-amber-500/60 to-amber-500/10",
  },
  large: {
    label: "Large (1-2+ hrs)",
    color: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
    bg: "from-red-500/60 to-red-500/10",
  },
};

export function AIScopePreview({ aiScoping, isCovered }: AIScopePreviewProps) {
  const bucketInfo =
    bucketConfig[aiScoping.estimate_bucket ?? "small"] || bucketConfig.small;

  // Fetch pricing to show estimated cost alongside the bucket
  const { data: pricing } = useQuery<PricingInfo>({
    queryKey: ["/api/intake/pricing"],
    queryFn: async () => {
      const res = await fetch("/api/intake/pricing", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch pricing");
      return res.json();
    },
    staleTime: 60_000,
  });

  const covered = isCovered ?? aiScoping.requiresPayment === false;
  const bucket = aiScoping.estimate_bucket as string;
  const paymentHoldPolicy = resolveIntakePaymentHoldPolicy(
    pricing?.intakePaymentHoldPolicy,
  );
  const paymentHoldAvailable = canOfferIntakePaymentHold({
    estimateBucket: bucket === "small" || bucket === "medium" || bucket === "large" ? bucket : undefined,
    isCovered: covered,
    policy: paymentHoldPolicy,
  });

  // Determine pricing display
  let priceDisplay: string | null = null;
  let priceNote: string | null = null;

  if (pricing && !covered) {
    if (bucket === "small") {
      priceDisplay = formatCurrency(pricing.smallTaskFee, pricing.currency);
      priceNote = "Fixed fee";
    } else if (bucket === "medium") {
      priceDisplay = formatCurrency(pricing.mediumTaskFee, pricing.currency);
      priceNote = "Fixed fee";
    } else if (bucket === "large") {
      priceDisplay = `From ${formatCurrency(pricing.hourlyRate, pricing.currency)}/hr`;
      priceNote = "Custom quote";
    }
  }

  return (
    <Card className="border-border overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-400">
      <div className={`h-1 bg-gradient-to-r ${bucketInfo.bg}`} />
      <CardContent className="pt-6 pb-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold font-heading mb-0.5">
              Request summary
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Here is the initial summary of the work based on the details you
              shared. We use automated triage in the background, but your
              request is still reviewed by our team before work starts.
            </p>
          </div>
        </div>

        {/* Scope Summary */}
        {aiScoping.scope_summary && aiScoping.scope_summary.length > 0 && (
          <div className="mb-5">
            <h4 className="text-xs font-semibold mb-3 flex items-center gap-1.5 uppercase tracking-wide text-muted-foreground">
              <Info className="w-3.5 h-3.5" />
              What we understand so far
            </h4>
            <ul className="space-y-2">
              {aiScoping.scope_summary.map((point: string, index: number) => (
                <li
                  key={index}
                  className="flex items-start gap-2.5 text-sm"
                >
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Estimate & Pricing */}
        <div className="mb-5 p-4 bg-accent/30 border border-border rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-foreground" />
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Effort and pricing path
            </h4>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold">
                {aiScoping.estimate_hours_number}h
              </span>
              <Badge className={bucketInfo.color}>{bucketInfo.label}</Badge>
            </div>
            <div className="text-right">
              {covered ? (
                <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-sm font-semibold">
                    Covered by your service
                  </span>
                </div>
              ) : priceDisplay ? (
                <div className="flex flex-col items-end">
                  <span className="text-xl font-bold">{priceDisplay}</span>
                  {priceNote && (
                    <span className="text-xs text-muted-foreground">
                      {priceNote}
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {/* Pricing context banner */}
          {!covered && pricing && (
            <div className="mt-3 pt-3 border-t border-border/50">
              {(bucket === "small" || bucket === "medium") &&
                paymentHoldAvailable &&
                paymentHoldPolicy === "required" && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <CreditCard className="w-3 h-3 shrink-0" />
                  A temporary hold will be placed on your card. You'll only be
                  charged once the work is completed.
                </p>
              )}
              {(bucket === "small" || bucket === "medium") &&
                paymentHoldAvailable &&
                paymentHoldPolicy === "optional" && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <CreditCard className="w-3 h-3 shrink-0" />
                  You can add a temporary card hold before submitting if you'd
                  like to authorise the work now.
                </p>
              )}
              {(bucket === "small" || bucket === "medium") &&
                paymentHoldAvailable &&
                paymentHoldPolicy === "optional_if_urgent" && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <CreditCard className="w-3 h-3 shrink-0" />
                  A card hold is optional by default and becomes required only
                  if you mark the request as urgent.
                </p>
              )}
              {bucket === "large" && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <FileText className="w-3 h-3 shrink-0" />
                  This will need a custom quote. Submit your request and we'll
                  get back to you with a detailed proposal.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Missing Info */}
        {aiScoping.missing_info && aiScoping.missing_info.length > 0 && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5 text-amber-700 dark:text-amber-400 uppercase tracking-wide">
              <AlertTriangle className="w-3.5 h-3.5" />
              Details we may still need
            </h4>
            <ul className="space-y-1">
              {aiScoping.missing_info.map((info: string, index: number) => (
                <li
                  key={index}
                  className="text-sm text-amber-700 dark:text-amber-400 pl-4"
                >
                  - {info}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Next Steps */}
        {aiScoping.recommended_next_steps && (
          <div className="pt-4 border-t border-border">
            <h4 className="text-xs font-semibold mb-2 uppercase tracking-wide text-muted-foreground">
              What happens next
            </h4>
            <p className="text-sm text-muted-foreground">
              {aiScoping.recommended_next_steps}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
